// Queries
import FETCH_MATCHES from "../../graphql/query/FetchMatches.gql";

// Mutations
import CREATE_MATCH from "../../graphql/mutation/CreateMatch.gql";
import UPDATE_MATCH from "../../graphql/mutation/UpdateMatch.gql";
import DELETE_MATCH from "../../graphql/mutation/DeleteMatch.gql";
import RESET_COACHEE_COACH_SELECTION from "../../graphql/mutation/ResetCoacheeCoachSelection.gql";

export const namespaced = true;

const initState = () => ({
  matches: [],
  total: 0,
  // Pending request counter instead of a boolean: overlapping actions would
  // otherwise clear the loading state while one is still in flight.
  pendingRequests: 0,
  // Monotonic id of the latest list request, so a slower earlier response
  // cannot overwrite the current one.
  lastFetchId: 0,
});

export const state = initState();

export const mutations = {
  SET_MATCHES(state, { items, total }) {
    state.matches = items;
    state.total = total;
  },
  START_REQUEST(state) {
    state.pendingRequests += 1;
  },
  END_REQUEST(state) {
    state.pendingRequests = Math.max(0, state.pendingRequests - 1);
  },
  SET_FETCH_ID(state, id) {
    state.lastFetchId = id;
  },
};

export const getters = {
  matches: (state) => state.matches,
  total: (state) => state.total,
  isLoading: (state) => state.pendingRequests > 0,
};

export const actions = {
  async fetchMatches({ commit, state }, { apolloClient, filters = {} }) {
    const fetchId = state.lastFetchId + 1;
    commit("SET_FETCH_ID", fetchId);
    try {
      commit("START_REQUEST");
      const {
        data: { FetchMatches },
      } = await apolloClient.query({
        query: FETCH_MATCHES,
        fetchPolicy: "no-cache",
        variables: { data: filters },
      });
      // A slower earlier request must not replace the current results.
      if (fetchId !== state.lastFetchId) return null;
      commit("SET_MATCHES", FetchMatches);
      return FetchMatches;
    } catch (error) {
      // A stale request must not surface an error for the current search.
      if (fetchId !== state.lastFetchId) return null;
      console.error(error);
      throw error;
    } finally {
      commit("END_REQUEST");
    }
  },

  async createMatch({ commit }, { apolloClient, match }) {
    try {
      commit("START_REQUEST");
      const {
        data: { CreateMatch },
      } = await apolloClient.mutate({
        mutation: CREATE_MATCH,
        fetchPolicy: "no-cache",
        variables: { data: match },
      });
      return CreateMatch;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("END_REQUEST");
    }
  },

  async updateMatch({ commit }, { apolloClient, _id, match }) {
    try {
      commit("START_REQUEST");
      const {
        data: { UpdateMatch },
      } = await apolloClient.mutate({
        mutation: UPDATE_MATCH,
        fetchPolicy: "no-cache",
        variables: { _id, data: match },
      });
      return UpdateMatch;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("END_REQUEST");
    }
  },

  async deleteMatch({ commit }, { apolloClient, _id }) {
    try {
      commit("START_REQUEST");
      const {
        data: { DeleteMatch },
      } = await apolloClient.mutate({
        mutation: DELETE_MATCH,
        fetchPolicy: "no-cache",
        variables: { _id },
      });
      return DeleteMatch;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("END_REQUEST");
    }
  },

  async resetCoacheeCoachSelection(
    { commit },
    { apolloClient, coacheeId, programId, cancelActiveSessions, force }
  ) {
    try {
      commit("START_REQUEST");
      const {
        data: { ResetCoacheeCoachSelection },
      } = await apolloClient.mutate({
        mutation: RESET_COACHEE_COACH_SELECTION,
        fetchPolicy: "no-cache",
        variables: { coacheeId, programId, cancelActiveSessions, force },
      });
      return ResetCoacheeCoachSelection;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("END_REQUEST");
    }
  },
};
